import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// A crash in ANY component unmounts the whole React tree. Without a boundary that
// leaves a white screen with no message and no way back — which is what a driver
// standing at the van at 5am actually sees, and what they then report as "the screen
// went blank". They have no reason to know that reloading would fix it.
//
// The boundary catches the throw and puts something honest on the screen instead: what
// happened, that their shift is still saved, and one big button that reloads. It never
// touches localStorage — the saved session ('hp-shift-session') is what makes a reload
// safe, and clearing it here would turn a render bug into a lost shift.
//
// Boundaries only catch errors thrown while RENDERING (and in lifecycle methods).
// Errors inside event handlers and promise callbacks never reach here, so this is a
// backstop for the unexpected, not a substitute for handling failures where they happen.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The stack is the only trace of this we get — a driver's phone has no console
    // anyone will ever read, but a Mac attached to it does, and so does a desktop user.
    // eslint-disable-next-line no-console
    console.error(`[${this.props.label || 'app'}] render crashed:`, error, info && info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const portal = this.props.variant === 'portal';
    const detail = (error && (error.message || error.toString())) || 'Unknown error';

    return (
      <div style={{
        minHeight: portal ? '100vh' : 'auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: portal ? '24px 20px' : '40px 20px',
        background: portal ? 'var(--hp-black)' : 'transparent',
        fontFamily: 'var(--font-body)',
      }}>
        <div style={{
          width: '100%', maxWidth: 420,
          background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
          borderRadius: 'var(--radius-lg, 14px)', padding: 24, textAlign: 'center',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: 'rgba(245,158,11,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
          }}>
            <AlertTriangle size={28} color="var(--hp-amber)" />
          </div>

          <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--hp-text)', marginBottom: 8 }}>
            Something went wrong
          </div>

          <p style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', lineHeight: 1.55, margin: '0 auto 18px', maxWidth: 320 }}>
            {portal
              ? 'This screen stopped working — it is not something you did. Your shift is saved on this phone, so reloading will pick it up where you left off.'
              : 'This page stopped working. Reloading is safe — nothing you have saved is affected.'}
          </p>

          <button
            onClick={() => window.location.reload()}
            style={{
              width: '100%', padding: '15px', background: 'var(--hp-gold)', border: 'none',
              borderRadius: 'var(--radius, 10px)', color: 'var(--hp-black)', fontWeight: 800,
              fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <RefreshCw size={17} /> Reload the page
          </button>

          {/* The message, plainly, so whoever is asked about it has something to go on —
              a driver can photograph this and it means something to us. */}
          <div style={{
            marginTop: 16, padding: '10px 12px', background: 'var(--hp-card)',
            border: '1px solid var(--hp-border)', borderRadius: 8,
            fontSize: '0.7rem', color: 'var(--hp-dim)', textAlign: 'left',
            fontFamily: 'var(--font-mono, monospace)', wordBreak: 'break-word',
          }}>
            {detail}
          </div>

          {portal && (
            <p style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 12, marginBottom: 0 }}>
              If it keeps happening, tell the office and show them this message.
            </p>
          )}
        </div>
      </div>
    );
  }
}
